import { PlacementType } from 'modules/AnchorAPI/v3/episodes/saveAdCuePoints';
import { TFunction } from 'client/i18n/hooks/useT';
import { NewCuePoint, ValidationError, SavedCuePoints, Ranges } from './types';

/**
 * get the highest and lowest timestamps for each placement type
 */
function getPlacementTypeRanges(savedCuePoints: SavedCuePoints): Ranges | null {
  if (savedCuePoints.length === 0) return null;
  const ranges: Ranges = {
    preRoll: { high: null, low: null },
    midRoll: { high: null, low: null },
    postRoll: { high: null, low: null },
  };
  savedCuePoints.forEach(cuePoint => {
    const { startTime, placementType } = cuePoint;
    const currentRange = ranges[placementType];
    const { high, low } = currentRange;
    if (high === null || startTime > high!) {
      currentRange.high = startTime;
    }
    if (low === null || startTime < low!) {
      currentRange.low = startTime;
    }
  });
  return ranges;
}

/**
 * validates that the new cue point is within the expected timestamp range
 * We expect:
 * - Pre-Roll ads to have a timestamp less than mid-roll and post-roll
 * - Mid-Roll ads to have a timestamp greater than pre-roll and less than post-roll
 * - Post-Roll ads to have a timestamp greater than pre-roll and mid-roll
 *
 * If existing ad slot value is null, we ignore it and treat as valid
 */
function getIsCuepointOrderValid(
  cuePoint: NewCuePoint,
  savedCuePoints: SavedCuePoints,
  t: TFunction
): string | null {
  const ranges = getPlacementTypeRanges(savedCuePoints);
  if (ranges === null) return null;
  const { preRoll, midRoll, postRoll } = ranges;
  const { startTime, startTimeString, placementType } = cuePoint;
  const comparisonStartTime =
    startTime || convertTimestampToMs(startTimeString!);
  switch (placementType) {
    case 'preRoll': {
      const isValid =
        (midRoll.low ? comparisonStartTime < midRoll.low : true) &&
        (postRoll.low ? comparisonStartTime < postRoll.low : true);
      return !isValid
        ? t(
            'EpisodeScreen.CuePoints.PreRollLocationInvalidMessage',
            'Pre-Roll ad timestamp must be set before mid-roll and post-roll ad timestamps.',
            'Ad insertion location invalid message'
          )
        : null;
    }
    case 'midRoll': {
      const isValid =
        (postRoll.low ? comparisonStartTime < postRoll.low : true) &&
        (preRoll.high ? comparisonStartTime > preRoll.high : true);
      return !isValid
        ? t(
            'EpisodeScreen.CuePoints.MidRollLocationInvalidMessage',
            'Mid-Roll ad timestamp must be set after pre-roll and before post-roll timestamps.',
            'Ad insertion location invalid message'
          )
        : null;
    }
    case 'postRoll': {
      const isValid =
        (midRoll.high ? comparisonStartTime > midRoll.high : true) &&
        (preRoll.high ? comparisonStartTime > preRoll.high : true);
      return !isValid
        ? t(
            'EpisodeScreen.CuePoints.PostRollLocationInvalidMessage',
            'Post-Roll ad timestamp must be set after pre-roll and mid-roll timestamps.',
            'Ad insertion location invalid message'
          )
        : null;
    }
    default:
      return null;
  }
}

function getIsValidCuePoint(
  cuePoint: NewCuePoint,
  savedCuePoints: SavedCuePoints,
  t: TFunction,
  mediaDuration?: number
): ValidationError[] | null {
  const { startTime, startTimeString, adCount, placementType } = cuePoint;
  const errors: ValidationError[] = [];

  // validate timestamp
  if (startTimeString === null)
    errors.push({
      type: 'startTime',
      message: t(
        'EpisodeScreen.CuePoints.TimestampRequiredMessage',
        'Timestamp is required.',
        'Timestamp required message'
      ),
    });
  if (startTimeString && !getIsValidTimestamp(startTimeString)) {
    errors.push({
      type: 'startTime',
      message: t(
        'EpisodeScreen.CuePoints.InvalidAdSlotMessage',
        'Invalid ad slot. Please enter all 9 digits in the following format: 00 (hr) : 00 (min) : 00 (sec) . 000 (msec).',
        'Invalid ad slot message'
      ),
    });
  }
  const isExistingTimestamp = savedCuePoints.some(
    savedCuePoint => savedCuePoint.startTimeString === cuePoint.startTimeString
  );
  if (isExistingTimestamp) {
    errors.push({
      type: 'startTime',
      message: t(
        'EpisodeScreen.CuePoints.TimestampAlreadyExistsMessage',
        'Timestamp already exists for this ad slot. Try adding a different ad slot.',
        'Timestamp already exists error message'
      ),
    });
  }
  const cuePointOrderError = getIsCuepointOrderValid(
    cuePoint,
    savedCuePoints,
    t
  );
  if (cuePointOrderError)
    errors.push({ type: 'startTime', message: cuePointOrderError });
  if (
    startTimeString &&
    !getIsTimestampWithinMediaDuration(
      startTime || convertTimestampToMs(startTimeString),
      mediaDuration
    )
  ) {
    errors.push({
      type: 'startTime',
      message: t(
        'EpisodeScreen.CuePoints.TimestampLongerThanEpisodeMessage',
        'Invalid ad slot. Timestamps must be less than the episode length.',
        'Timestamp longer than episode length error message'
      ),
    });
  }

  // validate ad counts
  if (adCount === 0)
    errors.push({
      type: 'adCount',
      message: t(
        'EpisodeScreen.CuePoints.AdCountRequiredMessage',
        'Ad count is required.',
        'Ad count required message'
      ),
    });
  if (adCount > 5)
    errors.push({
      type: 'adCount',
      message: t(
        'EpisodeScreen.CuePoints.AdCountHigherMessage',
        'Ad count must be 5 or less.',
        'Ad count higher validation message'
      ),
    });

  // validate placement type
  if (placementType === null)
    errors.push({
      type: 'placementType',
      message: t(
        'EpisodeScreen.CuePoints.AdTypeRequiredMessage',
        'Ad type is required.',
        'Ad type required message'
      ),
    });

  // pod count check
  const currentPlacementTypeCounts = {
    preRoll: 0,
    midRoll: 0,
    postRoll: 0,
  };

  savedCuePoints.forEach(savedCuePoint => {
    switch (savedCuePoint.placementType) {
      case 'preRoll':
        currentPlacementTypeCounts.preRoll =
          currentPlacementTypeCounts.preRoll + 1;
        break;
      case 'midRoll':
        currentPlacementTypeCounts.midRoll =
          currentPlacementTypeCounts.midRoll + 1;
        break;
      case 'postRoll':
        currentPlacementTypeCounts.postRoll =
          currentPlacementTypeCounts.postRoll + 1;
        break;
    }
  });

  switch (cuePoint.placementType) {
    case 'preRoll':
      if (currentPlacementTypeCounts.preRoll >= 1) {
        errors.push({
          type: 'form',
          message: t(
            'EpisodeScreen.CuePoints.PreRollOneSlotErrorMessage',
            'Pre-Roll ads can only have one slot.',
            'Ad entries error message'
          ),
        });
      }
      break;
    case 'midRoll':
      if (currentPlacementTypeCounts.midRoll >= 5) {
        errors.push({
          type: 'form',
          message: t(
            'EpisodeScreen.CuePoints.MidRollFiveSlotErrorMessage',
            'Mid-Roll ads can only have up to five slots.',
            'Ad entries error message'
          ),
        });
      }
      break;
    case 'postRoll':
      if (currentPlacementTypeCounts.postRoll >= 1) {
        errors.push({
          type: 'form',
          message: t(
            'EpisodeScreen.CuePoints.PostRollOneSlotErrorMessage',
            'Post-Roll ads can only have one slot.',
            'Ad entries error message'
          ),
        });
      }
      break;
  }
  return errors.length > 0 ? errors : null;
}

function getIsTimestampWithinMediaDuration(
  timestampInMs: number,
  mediaDuration?: number
) {
  if (!mediaDuration) return true;
  return timestampInMs < mediaDuration;
}

// Expects a format like: 34:59:59.432
function getIsValidTimestamp(timestamp: string) {
  return /^\d+[:]([0-5][0-9])[:]([0-5][0-9])(\.\d{1,3})?$/g.test(timestamp);
}

function convertTimestampToMs(timestamp: string) {
  const timeArray = timestamp.split(':').map(time => parseFloat(time));
  return timeArray.reduce((acc, time, index) => {
    // hours
    if (index === 0) {
      return acc + time * 3.6e6;
    }
    // minutes
    if (index === 1) {
      return acc + time * 60000;
    }
    // seconds
    if (index === 2) {
      return acc + time * 1000;
    }
    return acc;
  }, 0);
}

function getPlacementType(placementType: PlacementType, t: TFunction) {
  switch (placementType) {
    case 'preRoll':
      return t(
        'EpisodeScreen.CuePoints.PreRollOptionLabel',
        'Pre-Roll',
        'Ad insertion location option label'
      );
    case 'midRoll':
      return t(
        'EpisodeScreen.CuePoints.MidRollOptionLabel',
        'Mid-Roll',
        'Ad insertion location option label'
      );
    case 'postRoll':
    default:
      return t(
        'EpisodeScreen.CuePoints.PostRollOptionLabel',
        'Post-Roll',
        'Ad insertion location option label'
      );
  }
}

export {
  getPlacementType,
  getIsValidCuePoint,
  convertTimestampToMs,
  getIsTimestampWithinMediaDuration,
};
